import React, { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ChevronDown, HeartHandshake } from 'lucide-react';
import type { WeddingFamilyGroup } from '@/src/types';
import { Reveal } from './shared';

type Props = {
  groups: WeddingFamilyGroup[];
};

function sideLabel(side: WeddingFamilyGroup['side']) {
  return side === 'groom' ? 'Groom Side' : 'Bride Side';
}

export function FamilyTreeSection({ groups }: Props) {
  const visibleGroups = groups
    .map((group) => ({
      ...group,
      groupTitle: group.groupTitle || group.familyTitle || '',
      members: group.members.filter((member) => member.name?.trim() && member.relation?.trim()),
    }))
    .filter((group) => group.groupTitle?.trim() && group.members.length > 0);
  const [openKey, setOpenKey] = useState(() => (visibleGroups[0] ? `${visibleGroups[0].side}-${visibleGroups[0].groupTitle}` : ''));
  if (visibleGroups.length === 0) return null;

  return (
    <section className="bg-[#17140e] px-5 py-20 text-[#fffaf0] sm:px-8 lg:px-14 lg:py-28">
      <Reveal className="mb-12 max-w-5xl">
        <p className="text-xs font-semibold uppercase tracking-[0.26em] text-[#d7b56d]">Family Tree</p>
        <h2 className="mt-4 text-[clamp(2.35rem,5.8vw,6rem)] font-semibold leading-[0.98] tracking-normal">
          A celebration rooted in family.
        </h2>
      </Reveal>

      <div className="relative grid gap-5 lg:grid-cols-2">
        <div className="absolute left-1/2 top-8 hidden h-[calc(100%-4rem)] w-px bg-[#f7eee0]/14 lg:block" />
        {visibleGroups.map((group, index) => {
          const key = `${group.side}-${group.groupTitle}`;
          const isOpen = openKey === key;
          return (
            <Reveal key={key} delay={index * 0.05}>
              <motion.div
                layout
                className="relative overflow-hidden border border-[#f7eee0]/16 bg-[#f7eee0]/[0.035]"
                whileHover={{ borderColor: 'rgba(215,181,109,0.45)' }}
              >
                <button
                  type="button"
                  onClick={() => setOpenKey(isOpen ? '' : key)}
                  className="flex w-full items-center justify-between gap-5 p-5 text-left sm:p-7"
                >
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-[0.22em] text-[#d7b56d]">
                      {sideLabel(group.side)}
                    </span>
                    <span className="mt-2 block text-2xl font-semibold sm:text-3xl">{group.groupTitle}</span>
                  </span>
                  <motion.span animate={{ rotate: isOpen ? 180 : 0 }} className="grid h-10 w-10 place-items-center rounded-full border border-[#f7eee0]/18">
                    <ChevronDown size={18} />
                  </motion.span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.36, ease: 'easeOut' }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-6 sm:px-7">
                        <div className="relative ml-4 border-l border-[#d7b56d]/35 pl-7">
                          {group.members.map((member, memberIndex) => (
                            <motion.div
                              key={`${member.name}-${member.relation}`}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.28, delay: memberIndex * 0.04 }}
                              className="relative py-4"
                            >
                              <span className="absolute -left-[2.15rem] top-6 grid h-5 w-5 place-items-center rounded-full bg-[#17140e] text-[#d7b56d]">
                                <HeartHandshake size={13} />
                              </span>
                              <p className="text-lg font-semibold">{member.name}</p>
                              <p className="mt-1 text-sm uppercase tracking-[0.16em] text-[#f7eee0]/54">{member.relation}</p>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}
